import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';

import { t } from '@/i18n';
import { FIELD_APP_URL, FIELD_COMMAND_PUBLIC } from '@/utils/fieldApp';
const PublicFooter = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#1e3a5f] text-white" data-testid="public-footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img 
                src="/logo.png" 
                alt={t("All American Notice")} 
                className="h-12 w-auto brightness-0 invert"
              />
            </Link>
            <p className="text-slate-300 text-sm leading-relaxed mb-4">
              {t("Protecting your lien rights with automated preliminary notices, mechanic's liens, and bond claims across all 50 states.")}
            </p>
            <div className="flex items-center gap-2 text-[#F97316] font-semibold">
              <Phone className="h-4 w-4" />
              <a href="tel:561-680-2857">561-680-2857</a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="font-semibold text-white mb-4 text-lg">{t("Services")}</h4>
            <ul className="space-y-3 text-slate-300 text-sm">
              <li>
                <Link to="/florida-noc" className="text-[#F97316] font-semibold hover:text-white transition-colors" data-testid="footer-florida-noc-link">
                  {t("Florida Notice of Commencement")}
                </Link>
              </li>
              <li>
                <Link to="/services/preliminary-notices" className="hover:text-[#F97316] transition-colors">
                  {t("Preliminary Notices")}
                </Link>
              </li>
              <li>
                <Link to="/services/mechanics-liens" className="hover:text-[#F97316] transition-colors">
                  {t("Mechanic's Liens")}
                </Link>
              </li>
              <li>
                <Link to="/services/bond-claims" className="hover:text-[#F97316] transition-colors">
                  {t("Bond Claims")}
                </Link>
              </li>
              <li>
                <Link to="/services/notice-of-intent" className="hover:text-[#F97316] transition-colors">
                  {t("Notice of Intent to Lien")}
                </Link>
              </li>
              <li>
                <Link to="/services/research" className="hover:text-[#F97316] transition-colors">
                  {t("Research Services")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources Column */}
          <div>
            <h4 className="font-semibold text-white mb-4 text-lg">{t("Resources")}</h4>
            <ul className="space-y-3 text-slate-300 text-sm">
              <li>
                <Link to="/learning-center" className="hover:text-[#F97316] transition-colors">
                  {t("50 State Learning Center")}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="hover:text-[#F97316] transition-colors">
                  {t("Blog")}
                </Link>
              </li>
              <li>
                <Link to="/testimonials" className="hover:text-[#F97316] transition-colors">
                  {t("Testimonials")}
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="hover:text-[#F97316] transition-colors">
                  {t("Pricing")}
                </Link>
              </li>
              {FIELD_APP_URL && FIELD_COMMAND_PUBLIC && (
                <li>
                  <a href={FIELD_APP_URL} target="_blank" rel="noreferrer" className="hover:text-[#F97316] transition-colors" data-testid="footer-field-command-link">
                    {t("Field Command")} <span className="text-[10px] uppercase tracking-wide text-[#F97316] ml-1">{t("New")}</span>
                  </a>
                </li>
              )}
              <li>
                <Link to="/faq" className="hover:text-[#F97316] transition-colors">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-[#F97316] transition-colors">
                  {t("About Us")}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-[#F97316] transition-colors">
                  {t("Contact Us")}
                </Link>
              </li>
              <li>
                <Link to="/careers" className="text-[#F97316] font-semibold hover:text-white transition-colors" data-testid="footer-careers-link">
                  {t("Careers — we're hiring")}
                </Link>
              </li>
              <li>
                <Link to="/developers" className="hover:text-[#F97316] transition-colors" data-testid="footer-developers-link">
                  {t("Developers / API")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="font-semibold text-white mb-4 text-lg">{t("Contact Us")}</h4>
            <ul className="space-y-3 text-slate-300 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-[#F97316]" />
                <div>
                  <p className="font-medium text-white">{t("Phone")}</p>
                  <a href="tel:561-680-2857" className="hover:text-[#F97316] transition-colors">
                    561-680-2857
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-[#F97316]" />
                <div>
                  <p className="font-medium text-white">{t("Email")}</p>
                  <a href="mailto:info@allamericannotice.com" className="hover:text-[#F97316] transition-colors">
                    info@allamericannotice.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-[#F97316]" />
                <div>
                  <p className="font-medium text-white">{t("Address")}</p>
                  <p>{t("1732 S Congress Ave Suite 107")}</p>
                  <p>{t("Palm Springs, FL 33461")}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-400">
            <p>&copy; {currentYear} All American Notice, LLC. All rights reserved.</p>
            <a
              href="https://cworx.biz"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-built-by-cworx"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "9px 16px",
                background: "#F4F4F0",
                color: "#0A0A0A",
                fontFamily: "Arial, Helvetica, sans-serif",
                fontSize: "12px",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                textDecoration: "none",
              }}
            >
              <img
                src="https://cworx.biz/assets/icon-512.png"
                width="18"
                height="18"
                alt=""
                style={{ display: "block" }}
              />
              Built by&nbsp;<span style={{ color: "#FF4500", fontWeight: "bold" }}>Consulting WorX</span>
            </a>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="hover:text-white transition-colors">{t("Privacy Policy")}</Link>
              <Link to="/terms" className="hover:text-white transition-colors">{t("Terms of Service")}</Link>
              <Link to="/terms#sms-terms" className="hover:text-white transition-colors" data-testid="footer-sms-terms-link">{t("SMS Terms")}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;
